import { useEffect, useMemo, useState } from 'react';
import {
  Users, FolderKanban, Target, CalendarDays, Trophy, Activity,
  Plus, Copy, Check, Link2, X, Globe
} from 'lucide-react';
import api from '../services/api';
import { useNotifications } from './NotificationSystem';
import FlowIntegrations from './FlowIntegrations';

const StatCard = ({ icon: Icon, title, value, subtitle }) => (
  <div className="af-panel" style={{ padding: '1rem' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div>
        <div className="af-label" style={{ marginBottom: '0.35rem' }}>{title}</div>
        <div style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--accent)', fontFamily: 'inherit', lineHeight: 1 }}>{value}</div>
        {subtitle && <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.35rem' }}>{subtitle}</div>}
      </div>
      <div style={{
        padding: '0.5rem',
        background: 'color-mix(in srgb, var(--accent) 10%, transparent)',
        border: '1px solid color-mix(in srgb, var(--accent) 35%, transparent)',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Icon size={20} style={{ color: 'var(--accent)' }} />
      </div>
    </div>
  </div>
);

// Generic modal shell
const Modal = ({ isOpen, title, onClose, children, footer }) => {
  if (!isOpen) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 50, padding: '1rem',
      backdropFilter: 'blur(2px)',
    }}>
      <div style={{
        background: 'var(--panel)',
        border: '1px solid color-mix(in srgb, var(--line) 50%, transparent)',
        boxShadow: '0 0 2rem color-mix(in srgb, var(--line) 25%, transparent)',
        borderRadius: '6px',
        width: '100%',
        maxWidth: '440px',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.9rem 1.2rem',
          borderBottom: '1px solid color-mix(in srgb, var(--line) 25%, transparent)',
        }}>
          <span style={{ color: 'var(--accent)', fontFamily: 'inherit', fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600 }}>{title}</span>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', display: 'flex', padding: '2px' }}
          >
            <X size={16} />
          </button>
        </div>
        <div style={{ padding: '1rem 1.2rem', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>{children}</div>
        {footer && (
          <div style={{
            padding: '0.75rem 1.2rem',
            borderTop: '1px solid color-mix(in srgb, var(--line) 25%, transparent)',
            display: 'flex', justifyContent: 'flex-end', gap: '0.5rem',
          }}>{footer}</div>
        )}
      </div>
    </div>
  );
};

const Input = ({ label, ...props }) => (
  <div>
    {label && <label className="af-label" style={{ display: 'block', marginBottom: '0.4rem' }}>{label}</label>}
    <input className="af-input" style={{ width: '100%', boxSizing: 'border-box' }} {...props} />
  </div>
);

const Btn = ({ children, onClick, disabled, variant = 'primary', className = '' }) => {
  const variantClass =
    variant === 'primary'   ? 'af-btn af-btn-primary' :
    variant === 'secondary' ? 'af-btn af-btn-ghost'   :
    variant === 'danger'    ? 'af-btn af-btn-danger'  :
    variant === 'green'     ? 'af-btn af-btn-primary' :
    'af-btn af-btn-primary';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${variantClass} ${className}`}
      style={{ opacity: disabled ? 0.45 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      {children}
    </button>
  );
};

// Copyable invite/share link block
const CopyLink = ({ link }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback for non-HTTPS contexts: execCommand requires element to still be in DOM during copy
      const el = document.createElement('textarea');
      el.value = link;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.focus();
      el.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.5rem',
      background: 'color-mix(in srgb, var(--bg) 85%, black 15%)',
      border: '1px solid color-mix(in srgb, var(--line) 30%, transparent)',
      borderRadius: '4px',
      padding: '0.45rem 0.65rem',
    }}>
      <Link2 size={13} style={{ color: 'var(--muted)', flexShrink: 0 }} />
      <span style={{ flex: 1, fontSize: '0.75rem', color: 'var(--text)', fontFamily: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{link}</span>
      <button
        onClick={copy}
        className="af-btn af-btn-primary"
        style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem', display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 }}
      >
        {copied ? <Check size={11} /> : <Copy size={11} />}
        {copied ? 'Copied!' : 'Copy'}
      </button>
    </div>
  );
};

const Dashboard = ({ onQuickLoadProject, onTestProject, onLoadFlow, onNewFlow }) => {
  const { showSuccess, showError, showWarning } = useNotifications();
  const [data, setData] = useState({ stats: {}, teams: [], projects: [], goals: [] });
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickLoadId, setQuickLoadId] = useState('');
  const [testFlowId, setTestFlowId] = useState('');
  const [showIntegrations, setShowIntegrations] = useState(false);

  // Create modals
  const [teamModal, setTeamModal] = useState({ open: false, name: '', members: 1 });
  const [projectModal, setProjectModal] = useState({ open: false, name: '', due_date: '', description: '' });
  const [goalModal, setGoalModal] = useState({ open: false, name: '', progress: 0, deadline: '' });

  // Post-create modals
  const [inviteModal, setInviteModal] = useState({ open: false, teamName: '', teamId: '', memberCount: 0 });
  const [shareModal, setShareModal] = useState({ open: false, title: '', entityId: '', type: '' });

  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.getDashboard().catch(() => ({ stats: {}, teams: [], projects: [], goals: [] })),
      api.getFlows().catch(() => []),
    ]).then(([d, f]) => {
      if (mounted) { setData(d); setFlows(f); }
    }).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const stats = useMemo(() => [
    { icon: Users,        title: 'Active Users', value: data.stats.active_users ?? '—', subtitle: 'Registered' },
    { icon: FolderKanban, title: 'Projects',      value: data.stats.projects ?? '—',     subtitle: `${data.projects.length} total` },
    { icon: Target,       title: 'Goals',         value: `${data.stats.goals_progress ?? 0}%`, subtitle: 'Quarter progress' },
    { icon: Activity,     title: 'Executions',    value: data.stats.executions ?? '—',   subtitle: 'This month' },
  ], [data]);

  const inviteLink  = (id) => `${window.location.origin}?invite=${id}`;
  const shareLink   = (id) => `${window.location.origin}?share=${id}`;

  // ── Create Team ───────────────────────────────────────────────────────────
  const createTeam = async () => {
    if (!teamModal.name.trim()) { showError('Please enter a team name.', 'Validation Error'); return; }
    setCreating(true);
    try {
      const created = await api.createTeam({ name: teamModal.name });
      setData(prev => ({ ...prev, teams: [...prev.teams, { ...created, members: 0 }] }));
      const memberCount = Number(teamModal.members);
      setTeamModal({ open: false, name: '', members: 1 });
      if (memberCount > 1) {
        setInviteModal({ open: true, teamName: created.name, teamId: created.id, memberCount });
      } else {
        showSuccess(`Team "${created.name}" created!`, 'Team Created');
      }
    } catch {
      showError('Failed to create team. Please try again.', 'Error');
    } finally {
      setCreating(false);
    }
  };

  // ── Create Project ────────────────────────────────────────────────────────
  const createProject = async () => {
    if (!projectModal.name.trim()) { showError('Please enter a project name.', 'Validation Error'); return; }
    setCreating(true);
    try {
      const created = await api.createProject({
        name: projectModal.name,
        due_date: projectModal.due_date || null,
        status: 'in_progress',
      });
      setData(prev => ({ ...prev, projects: [...prev.projects, created] }));
      setProjectModal({ open: false, name: '', due_date: '', description: '' });
      setShareModal({ open: true, title: created.name, entityId: created.id, type: 'project' });
    } catch {
      showError('Failed to create project. Please try again.', 'Error');
    } finally {
      setCreating(false);
    }
  };

  // ── Create Goal ───────────────────────────────────────────────────────────
  const createGoal = async () => {
    if (!goalModal.name.trim()) { showError('Please enter a goal name.', 'Validation Error'); return; }
    setCreating(true);
    try {
      const created = await api.createGoal({
        name: goalModal.name,
        progress: Math.min(100, Math.max(0, Number(goalModal.progress) || 0)),
        deadline: goalModal.deadline || null,
      });
      setData(prev => ({ ...prev, goals: [...prev.goals, created] }));
      setGoalModal({ open: false, name: '', progress: 0, deadline: '' });
      showSuccess(`Goal "${created.name}" created!`, 'Goal Created');
    } catch {
      showError('Failed to create goal. Please try again.', 'Error');
    } finally {
      setCreating(false);
    }
  };

  // ── Test Flow ─────────────────────────────────────────────────────────────
  const handleTestFlow = () => {
    const p = (data.projects || []).find(x => x.id === testFlowId);
    if (!p) return;
    if (!p.flow?.nodes?.length) {
      showWarning(
        `"${p.name}" has no saved flow yet. Load it in the Builder, build your flow, then save it before testing.`,
        'No Flow Found'
      );
      return;
    }
    if (onTestProject) onTestProject(p);
    else onQuickLoadProject?.(p);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '16rem', background: 'var(--bg)' }}>
        <div style={{ textAlign: 'center', color: 'var(--muted)', fontSize: '0.78rem' }}>
          <div style={{
            width: '2rem', height: '2rem',
            border: '2px solid color-mix(in srgb, var(--line) 30%, transparent)',
            borderTopColor: 'var(--accent)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 0.75rem',
          }} />
          Loading dashboard…
        </div>
      </div>
    );
  }

  const selectStyle = {
    flex: 1,
    background: 'color-mix(in srgb, var(--bg) 85%, black 15%)',
    border: '1px solid color-mix(in srgb, var(--line) 30%, transparent)',
    color: 'var(--text)',
    fontFamily: 'inherit',
    fontSize: '0.75rem',
    padding: '0.35rem 0.6rem',
    borderRadius: '4px',
    outline: 'none',
  };

  const smallBtnStyle = {
    display: 'flex', alignItems: 'center', gap: '0.25rem',
    fontSize: '0.72rem', padding: '0.25rem 0.55rem',
  };

  return (
    <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem', background: 'var(--bg)', minHeight: '100%' }}>
      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
        {stats.map((s, i) => <StatCard key={i} {...s} />)}
      </div>

      {/* Goals progress + side lists */}
      <div className="dashboard-mid-grid">
        {/* Goals progress panel */}
        <div className="af-panel" style={{ padding: '1.1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <CalendarDays size={16} style={{ color: 'var(--accent-2)' }} />
              <span style={{ color: 'var(--accent-2)', fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 600 }}>Goals &amp; Progress</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Trophy size={15} style={{ color: 'var(--accent)' }} />
              <button
                onClick={() => setGoalModal(s => ({ ...s, open: true }))}
                className="af-btn"
                style={smallBtnStyle}
              >
                <Plus size={11} /> New Goal
              </button>
            </div>
          </div>
          {(data.goals || []).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--muted)', fontSize: '0.75rem' }}>
              No goals yet. Create your first goal to track progress.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
              {(data.goals || []).slice(0, 8).map((g) => (
                <div key={g.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginBottom: '0.3rem' }}>
                    <span style={{ color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>{g.name}</span>
                    <span style={{ color: 'var(--muted)', flexShrink: 0 }}>{g.progress}%{g.deadline ? ` · ${g.deadline}` : ''}</span>
                  </div>
                  <div style={{ width: '100%', background: 'color-mix(in srgb, var(--line) 15%, transparent)', height: '4px', borderRadius: '2px', overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '4px',
                        borderRadius: '2px',
                        width: `${Math.min(100, g.progress)}%`,
                        background:
                          g.progress >= 100 ? 'var(--accent)' :
                          g.progress >= 60  ? 'var(--accent-2)' :
                          'color-mix(in srgb, var(--accent) 60%, var(--accent-2) 40%)',
                        transition: 'width 0.3s',
                        boxShadow: '0 0 6px color-mix(in srgb, var(--accent) 40%, transparent)',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right side lists */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {/* Teams */}
          <div className="af-panel" style={{ padding: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <span style={{ color: 'var(--accent-2)', fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 600 }}>Teams</span>
              <button
                onClick={() => setTeamModal(s => ({ ...s, open: true }))}
                className="af-btn"
                style={smallBtnStyle}
              >
                <Plus size={11} /> Create Team
              </button>
            </div>
            {(data.teams || []).length === 0 ? (
              <p style={{ fontSize: '0.78rem', color: 'var(--muted)', margin: 0 }}>No teams yet.</p>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {(data.teams || []).map((t, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.75rem', borderLeft: '2px solid color-mix(in srgb, var(--accent) 40%, transparent)', paddingLeft: '0.5rem' }}>
                    <span style={{ color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                    <span style={{ color: 'var(--muted)', fontSize: '0.75rem', flexShrink: 0 }}>{t.members} member{t.members !== 1 ? 's' : ''}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Projects */}
          <div className="af-panel" style={{ padding: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <span style={{ color: 'var(--accent-2)', fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 600 }}>Projects</span>
              <button
                onClick={() => setProjectModal(s => ({ ...s, open: true }))}
                className="af-btn"
                style={smallBtnStyle}
              >
                <Plus size={11} /> Create Project
              </button>
            </div>
            {(data.projects || []).length === 0 ? (
              <p style={{ fontSize: '0.78rem', color: 'var(--muted)', margin: 0 }}>No projects yet.</p>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {(data.projects || []).slice(0, 6).map((p, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.75rem', borderLeft: '2px solid color-mix(in srgb, var(--accent-2) 40%, transparent)', paddingLeft: '0.5rem' }}>
                    <span style={{ color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>{p.name}</span>
                    <span style={{ color: 'var(--muted)', fontSize: '0.75rem', flexShrink: 0 }}>{p.due_date ? `Due ${p.due_date}` : p.status || 'active'}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* My Flows */}
          <div className="af-panel" style={{ padding: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <span style={{ color: 'var(--accent)', fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 600 }}>My Flows</span>
              <button
                onClick={() => onNewFlow && onNewFlow()}
                className="af-btn af-btn-primary"
                style={smallBtnStyle}
              >
                <Plus size={11} /> New Flow
              </button>
            </div>
            {flows.length === 0 ? (
              <p style={{ fontSize: '0.78rem', color: 'var(--muted)', margin: 0 }}>No saved flows yet. Build one and click Save.</p>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {flows.slice(0, 8).map((f) => (
                  <li
                    key={f.id}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.75rem', borderLeft: '2px solid color-mix(in srgb, var(--accent) 50%, transparent)', paddingLeft: '0.5rem', cursor: 'pointer' }}
                    onClick={() => onLoadFlow && onLoadFlow(f)}
                  >
                    <span style={{ color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>{f.name}</span>
                    <span style={{ color: 'var(--muted)', fontSize: '0.75rem', flexShrink: 0 }}>
                      {f.updated_at ? new Date(f.updated_at).toLocaleDateString() : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Bottom action panels */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
        {/* Quick Load */}
        <div className="af-panel" style={{ padding: '1rem' }}>
          <div style={{ color: 'var(--accent-2)', fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.75rem' }}>Quick Load Project</div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <select
              style={selectStyle}
              value={quickLoadId}
              onChange={(e) => setQuickLoadId(e.target.value)}
            >
              <option value="" disabled>Select a project…</option>
              {(data.projects || []).map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <Btn
              onClick={() => {
                const p = (data.projects || []).find(x => x.id === quickLoadId);
                if (p && onQuickLoadProject) onQuickLoadProject(p);
              }}
              disabled={!quickLoadId}
            >Load</Btn>
          </div>
        </div>

        {/* Test a Flow */}
        <div className="af-panel" style={{ padding: '1rem' }}>
          <div style={{ color: 'var(--accent-2)', fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.3rem' }}>Test a Flow</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.75rem' }}>Preview the customer-facing version of a flow.</div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <select
              style={selectStyle}
              value={testFlowId}
              onChange={(e) => setTestFlowId(e.target.value)}
            >
              <option value="" disabled>Select a project…</option>
              {(data.projects || []).map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}{!p.flow?.nodes?.length ? ' (no flow)' : ''}
                </option>
              ))}
            </select>
            <Btn
              variant="green"
              onClick={handleTestFlow}
              disabled={!testFlowId}
            >Test</Btn>
          </div>
        </div>

        {/* Flow Integrations */}
        <div className="af-panel" style={{ padding: '1rem' }}>
          <div style={{ color: 'var(--accent-2)', fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.3rem' }}>Flow Integrations</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.75rem' }}>Connect your flows to Slack, webhooks, CRMs, and more.</div>
          <button
            onClick={() => setShowIntegrations(true)}
            className="af-btn af-btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
          >
            <Globe size={13} />
            Manage Integrations
          </button>
        </div>
      </div>

      {/* ── Modals ─────────────────────────────────────────────────────────── */}

      {/* Create Team */}
      <Modal
        isOpen={teamModal.open}
        title="Create Team"
        onClose={() => setTeamModal({ open: false, name: '', members: 1 })}
        footer={
          <>
            <Btn variant="secondary" onClick={() => setTeamModal({ open: false, name: '', members: 1 })}>Cancel</Btn>
            <Btn onClick={createTeam} disabled={creating || !teamModal.name.trim()}>
              {creating ? 'Creating…' : 'Create Team'}
            </Btn>
          </>
        }
      >
        <Input
          label="Team Name"
          value={teamModal.name}
          onChange={(e) => setTeamModal(s => ({ ...s, name: e.target.value }))}
          placeholder="e.g. Marketing Team"
          autoFocus
          onKeyDown={(e) => e.key === 'Enter' && createTeam()}
        />
        <Input
          label="Team Size (including you)"
          type="number"
          min="1"
          max="100"
          value={teamModal.members}
          onChange={(e) => setTeamModal(s => ({ ...s, members: e.target.value }))}
          placeholder="1"
        />
        {Number(teamModal.members) > 1 && (
          <div style={{
            fontSize: '0.78rem',
            color: 'var(--accent)',
            background: 'color-mix(in srgb, var(--accent) 8%, transparent)',
            border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)',
            borderRadius: '4px',
            padding: '0.5rem 0.75rem',
          }}>
            An invite link will be generated after creation so you can add your other {Number(teamModal.members) - 1} member{Number(teamModal.members) - 1 !== 1 ? 's' : ''}.
          </div>
        )}
      </Modal>

      {/* Invite Members */}
      <Modal
        isOpen={inviteModal.open}
        title={`Invite Members — ${inviteModal.teamName}`}
        onClose={() => setInviteModal({ open: false, teamName: '', teamId: '', memberCount: 0 })}
        footer={
          <Btn onClick={() => setInviteModal({ open: false, teamName: '', teamId: '', memberCount: 0 })}>Done</Btn>
        }
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '0.75rem',
          background: 'color-mix(in srgb, var(--accent) 8%, transparent)',
          border: '1px solid color-mix(in srgb, var(--accent) 30%, transparent)',
          borderRadius: '4px',
        }}>
          <div style={{
            width: '2rem', height: '2rem',
            background: 'color-mix(in srgb, var(--accent) 15%, transparent)',
            border: '1px solid color-mix(in srgb, var(--accent) 35%, transparent)',
            borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Users size={14} style={{ color: 'var(--accent)' }} />
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--accent)' }}>Team created!</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
              You need {inviteModal.memberCount - 1} more member{inviteModal.memberCount - 1 !== 1 ? 's' : ''}.
            </div>
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text)', marginBottom: '0.5rem' }}>Share this invite link:</div>
          <CopyLink link={inviteLink(inviteModal.teamId)} />
          <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.4rem' }}>
            Send this link to your team members. They can join the team from the AI Flow app.
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text)', marginBottom: '0.5rem' }}>Or share via email:</div>
          <CopyLink link={`Hi! Join my team "${inviteModal.teamName}" on AI Flow: ${inviteLink(inviteModal.teamId)}`} />
        </div>
      </Modal>

      {/* Create Project */}
      <Modal
        isOpen={projectModal.open}
        title="Create Project"
        onClose={() => setProjectModal({ open: false, name: '', due_date: '', description: '' })}
        footer={
          <>
            <Btn variant="secondary" onClick={() => setProjectModal({ open: false, name: '', due_date: '', description: '' })}>Cancel</Btn>
            <Btn onClick={createProject} disabled={creating || !projectModal.name.trim()}>
              {creating ? 'Creating…' : 'Create Project'}
            </Btn>
          </>
        }
      >
        <Input
          label="Project Name"
          value={projectModal.name}
          onChange={(e) => setProjectModal(s => ({ ...s, name: e.target.value }))}
          placeholder="e.g. Customer Support Bot"
          autoFocus
          onKeyDown={(e) => e.key === 'Enter' && createProject()}
        />
        <Input
          label="Due Date (optional)"
          type="date"
          value={projectModal.due_date}
          onChange={(e) => setProjectModal(s => ({ ...s, due_date: e.target.value }))}
        />
        <div style={{
          fontSize: '0.75rem',
          color: 'var(--muted)',
          background: 'color-mix(in srgb, var(--line) 8%, transparent)',
          border: '1px solid color-mix(in srgb, var(--line) 20%, transparent)',
          borderRadius: '4px',
          padding: '0.6rem 0.75rem',
        }}>
          After creating, load the project in Builder to build and save your flow, then use "Test a Flow" to preview it.
        </div>
      </Modal>

      {/* Project / entity Share modal */}
      <Modal
        isOpen={shareModal.open}
        title={shareModal.type === 'project' ? 'Project Created!' : 'Created!'}
        onClose={() => setShareModal({ open: false, title: '', entityId: '', type: '' })}
        footer={
          <Btn onClick={() => setShareModal({ open: false, title: '', entityId: '', type: '' })}>Done</Btn>
        }
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '0.75rem',
          background: 'color-mix(in srgb, var(--accent-2) 8%, transparent)',
          border: '1px solid color-mix(in srgb, var(--accent-2) 30%, transparent)',
          borderRadius: '4px',
        }}>
          <div style={{
            width: '2rem', height: '2rem',
            background: 'color-mix(in srgb, var(--accent-2) 15%, transparent)',
            border: '1px solid color-mix(in srgb, var(--accent-2) 35%, transparent)',
            borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <FolderKanban size={14} style={{ color: 'var(--accent-2)' }} />
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--accent-2)' }}>"{shareModal.title}" is ready!</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>Share the link below to collaborate.</div>
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text)', marginBottom: '0.5rem' }}>Project link:</div>
          <CopyLink link={shareLink(shareModal.entityId)} />
          <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.4rem' }}>
            Go to Builder, build your flow, then save it to enable "Test a Flow" for this project.
          </div>
        </div>
      </Modal>

      {/* Create Goal */}
      <Modal
        isOpen={goalModal.open}
        title="Create Goal"
        onClose={() => setGoalModal({ open: false, name: '', progress: 0, deadline: '' })}
        footer={
          <>
            <Btn variant="secondary" onClick={() => setGoalModal({ open: false, name: '', progress: 0, deadline: '' })}>Cancel</Btn>
            <Btn onClick={createGoal} disabled={creating || !goalModal.name.trim()}>
              {creating ? 'Creating…' : 'Create Goal'}
            </Btn>
          </>
        }
      >
        <Input
          label="Goal Name"
          value={goalModal.name}
          onChange={(e) => setGoalModal(s => ({ ...s, name: e.target.value }))}
          placeholder="e.g. Deploy chatbot to 100 customers"
          autoFocus
          onKeyDown={(e) => e.key === 'Enter' && createGoal()}
        />
        <div>
          <label className="af-label" style={{ display: 'block', marginBottom: '0.4rem' }}>
            Starting Progress: <span style={{ color: 'var(--accent)' }}>{goalModal.progress}%</span>
          </label>
          <input
            type="range"
            min="0"
            max="100"
            value={goalModal.progress}
            onChange={(e) => setGoalModal(s => ({ ...s, progress: e.target.value }))}
            style={{ width: '100%', accentColor: 'var(--accent)' }}
          />
        </div>
        <Input
          label="Deadline (optional)"
          type="date"
          value={goalModal.deadline}
          onChange={(e) => setGoalModal(s => ({ ...s, deadline: e.target.value }))}
        />
      </Modal>

      {/* Flow Integrations modal */}
      {showIntegrations && (
        <FlowIntegrations
          flow={{ id: 'current', nodes: [], edges: [] }}
          onClose={() => setShowIntegrations(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;
